import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CustomerOption {
  id: number
  name: string
  companyName: string | null
}

interface CustomerContextState {
  ownerUserId: number | null
  customers: CustomerOption[]
  currentCustomerId: number | null
  selectionRequired: boolean
  setCustomers: (ownerUserId: number, customers: CustomerOption[]) => void
  selectCustomer: (customerId: number) => void
  requireSelection: () => void
  updateCustomer: (customer: CustomerOption) => void
  reset: () => void
}

const emptyState = {
  ownerUserId: null,
  customers: [],
  currentCustomerId: null,
  selectionRequired: false,
}

export const resolveCustomerSelection = (
  ownerUserId: number,
  customers: CustomerOption[],
  previousOwnerUserId: number | null,
  previousCustomerId: number | null,
) => {
  const previousId = previousOwnerUserId === ownerUserId ? previousCustomerId : null
  return customers.some((customer) => customer.id === previousId)
    ? previousId
    : customers.length === 1
      ? customers[0].id
      : null
}

export const requiresCustomerSelection = (customers: CustomerOption[], currentCustomerId: number | null) =>
  currentCustomerId == null && customers.length !== 1

export const useCustomerContextStore = create<CustomerContextState>()(
  persist(
    (set, get) => ({
      ...emptyState,
      setCustomers: (ownerUserId, customers) => {
        const previous = get()
        const currentCustomerId = resolveCustomerSelection(
          ownerUserId,
          customers,
          previous.ownerUserId,
          previous.currentCustomerId,
        )
        set({
          ownerUserId,
          customers,
          currentCustomerId,
          selectionRequired: requiresCustomerSelection(customers, currentCustomerId),
        })
      },
      selectCustomer: (currentCustomerId) => set({ currentCustomerId, selectionRequired: false }),
      requireSelection: () => set({ currentCustomerId: null, selectionRequired: true }),
      updateCustomer: (customer) =>
        set((state) => ({
          customers: state.customers.map((item) => (item.id === customer.id ? customer : item)),
        })),
      reset: () => set(emptyState),
    }),
    {
      name: 'customerContextStore',
      partialize: ({ ownerUserId, customers, currentCustomerId }) => ({
        ownerUserId,
        customers,
        currentCustomerId,
      }),
    },
  ),
)

export const CUSTOMER_SELECTION_REQUIRED_EVENT = 'dayu:customer-selection-required'

export const requireCustomerSelection = () => {
  useCustomerContextStore.getState().requireSelection()
  window.dispatchEvent(new Event(CUSTOMER_SELECTION_REQUIRED_EVENT))
}
